<template>
  <div class="input-checkbox" :class="{ small }">
    <input
      ref="checkbox"
      type="checkbox"
      :id="iuid"
      :value="value"
      :checked="isChecked"
      :disabled="disabled"
      :tabindex="tabindex"
      @click="handleChange($event)"
      @change="handleChange($event)"
      @blur="handleBlur($event)"
    />
    <label
      :for="iuid"
      tabindex="-1"
      :title="tooltip"
      v-html="label"
      @click="handleChange($event)"
    />
  </div>
</template>

<script>
  let uid = 0;

  export default {
    name: 'Checkbox',
    components: {
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: [String, Number],
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      checked: {
        type: [Boolean, Array, String],
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      tabindex: {
        type: Number,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      small: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        iuid: `input-checkbox-${uid}`,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      invalid() {
        return (this.vuelidate && this.vuelidate.$dirty && this.vuelidate.$invalid);
      },
      isChecked() {
        if (Array.isArray(this.checked) && this.value !== null && this.value !== undefined) {
          return this.checked.includes(this.value);
        }
        if (this.value !== null && this.value !== undefined) {
          return this.checked === this.value;
        }
        return this.checked;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {},
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      toggle() {
        const { checkbox } = this.$refs;
        checkbox.checked = !checkbox.checked;
        this.update();
      },
      update() {
        const { checkbox } = this.$refs;
        let value = checkbox.checked;

        if (Array.isArray(this.checked) && this.value !== null && this.value !== undefined) {
          value = this.checked.slice();
          const idx = value.indexOf(this.value);
          if (checkbox.checked && idx === -1) {
            value.push(this.value);
          }
          if (!checkbox.checked && idx !== -1) {
            value.splice(idx, 1);
          }
        } else if (this.value !== null && this.value !== undefined) {
          value = checkbox.checked ? this.value : undefined;
        }

        this.$emit('update:checked', value);
        this.$emit('input', value, this.value);
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleChange(evt) {
        evt.stopPropagation();
        if (evt.type !== 'click') {
          this.update();
        }
      },
      handleBlur(evt) {
        if (this.vuelidate) {
          this.vuelidate.$touch();
        }
        this.$emit('blur', evt);
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-checkbox {
  display: inline-block;
  min-height: 22px;
  min-width: 16px;

  input[type=checkbox] {
    position: absolute;
    z-index: -1;
    opacity: 0;
    outline: 0;

    & + label {
      display: inline-block;
      position: relative;
      padding-left: 24px;
      -webkit-tap-highlight-color: transparent;
      @include font-body-medium;
      color: $color-black-alpha-80;
      user-select: none;
      cursor: pointer;
      outline: 0;

      &:empty {
        min-height: 16px;
        padding-Left: 0;
      }
    }

    /* Box */
    & + label:before {
      position: absolute;
      content: "";
      left: 0;
      top: 3px;
      width: 16px;
      height: 16px;
      border-width: 1px;
      border-style: solid;
      border-color: $color-black-solid-100;
      border-radius: 4px;
      background-color: transparent;
      will-change: background-color, border-color;
      transition: background-color $default-transition, border-color $default-transition;
    }

    &:checked {
      & + label:before {
        background-color: $color-black-solid-100;
      }
    }

    &:hover {
      &:not([disabled]) + label:before {
        border-color: currentColor;
      }
    }

    &:focus {
      & + label:before {
        // animation: blip $default-transition;
      }
    }

    /* Checkmark */
    & + label:after {
      position: absolute;
      content: '';
      left: 5px;
      top: 6px;
      width: 5px;
      height: 8px;
      border-top: 1px solid $color-white-solid-10;
      border-left: 1px solid $color-white-solid-10;
      transform: translate(0.5px, 0) scale(0,1) rotate(220deg);
      will-change: transform;
      transition: transform $default-transition;
    }

    &:checked + label:after {
      transform: translate(0.5px, 0) scale(1,1) rotate(220deg);
    }
  }

  &.survey {
    input[type=checkbox] {
      & + label {
        color: black;
      }

      /* Box */
      & + label:before {
        background: white;
        border-color: $color-black-alpha-40;
      }

      &:checked {
        & + label:before {
          background-color: $color-blue-solid-100;
          border-color: $color-black-alpha-40;
        }
      }

      &:hover {
        &:not([disabled]) + label:before {
          border-color: currentColor;
        }
      }
    }
  }

  &.changed {
    input[type=checkbox] {
      & + label:before {
        box-shadow: 0 0 1px 1px rgba(255, 255, 255, 0.8), 0 0 5px 3px rgba(214, 16, 16, 0.8);
      }
    }
  }
}
</style>
